// A lot of TextFields on the same page can be heavy to manage for the browser.
// On order to avoid overload it, the table cells are load as simple divs containing text,
// and they become TextFields on hover or on focus.
// This component handle this dynamic logic.

import { memo, useCallback, useEffect, useState } from 'react';

import { InputProps as StandardInputProps } from '@material-ui/core/Input';
import { TableCellProps } from '@material-ui/core/TableCell';
import { TextFieldProps } from '@material-ui/core/TextField';

import { StyledDiv, StyledTableCell, StyledTestField } from './styled';

interface Props {
    id: number;
    getExternalValue: () => string;
    setExternalValue: (value: string) => void;
    setExternalDirtyCell: () => void;
    shouldFocusMe: boolean;
    setShouldFocusCellId: (value: number | null) => void;
    editDisabled: boolean;
    isCustomer: boolean;
    formatInternalValue?: (value: string) => string;
    tableCellProps?: Omit<TableCellProps, 'onMouseEnter' | 'onMouseLeave'>;
    textFieldProps?: Omit<TextFieldProps, 'size' | 'variant' | 'inputProps'>;
    inputProps?: StandardInputProps['inputProps'];
    divStyle: React.CSSProperties;
}

const KpiDynamicCell = (props: Props) => {
    const { id, getExternalValue, setExternalValue, setExternalDirtyCell, shouldFocusMe, setShouldFocusCellId, editDisabled, isCustomer, formatInternalValue, tableCellProps, textFieldProps, inputProps, divStyle } = props;

    const [hasOver, setHasOver] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);

    const [internalData, setInternalData] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    const [shouldAutofocus, setShouldAutofocus] = useState(false);

    const isActive = hasOver || hasFocus;

    useEffect(() => {
        if (isDirty && hasFocus) {
            const timeout = setTimeout(() => {
                setExternalValue(internalData);
                setIsDirty(false);
            }, 5000);

            return () => clearTimeout(timeout);
        }
    }, [setExternalValue, internalData, isDirty, hasFocus]);

    useEffect(() => {
        if (!isDirty) {
            setInternalData(getExternalValue());
        }
    }, [isDirty, getExternalValue]);

    useEffect(() => {
        if (shouldFocusMe) {
            setShouldAutofocus(true);
            setHasFocus(true);
            setShouldFocusCellId(null);
        }
    }, [shouldFocusMe, setShouldFocusCellId]);

    useEffect(() => {
        if (!hasFocus && isDirty) {
            setExternalValue(internalData);
            setIsDirty(false);
        }

        if (!isActive) {
            setShouldAutofocus(false);
        }
    }, [isActive, hasFocus, isDirty, setExternalValue, internalData]);

    const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
            // prevent insertion of newlines
            e.preventDefault();
        }

        if (e.key === 'Tab' || e.keyCode === 9) {
            // move focus to the next cell
            setShouldFocusCellId(id + 1);

            if (e.target instanceof HTMLElement) e.target.blur();
            e.preventDefault();
        }
    }, [id, setShouldFocusCellId]);

    const onFocus = useCallback(() => {
        setHasFocus(true);
    }, []);

    const onBlur = useCallback(() => {
        setHasFocus(false);
    }, []);

    return (
        <StyledTableCell onMouseEnter={() => setHasOver(true)} onMouseLeave={() => setHasOver(false)} {...tableCellProps}>
            {((hasOver || hasFocus) && !editDisabled && isCustomer) ? (
                <StyledTestField
                    size='small'
                    variant='outlined'
                    inputProps={{ ...inputProps, onKeyDown, onFocus, onBlur, autoFocus: shouldAutofocus }}
                    value={internalData}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        setInternalData(e.target.value.replaceAll('\n', ' '));
                        setIsDirty(true);
                        setExternalDirtyCell();
                    }}
                    {...textFieldProps}
                />
            ) : (
                <StyledDiv style={divStyle}>
                    {formatInternalValue ? formatInternalValue(internalData) : internalData}
                </StyledDiv>
            )}
        </StyledTableCell>
    );
};

export default memo(KpiDynamicCell);